import React, { useCallback, useMemo, useState } from 'react';
import { View, StyleSheet, Pressable, Platform, ActivityIndicator } from 'react-native';
import { Image } from 'expo-image';
import PropTypes from 'prop-types';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useToastStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { download3DAsset } from '@/constants/utils/model3DDownload';
import Model3DViewer, { isModel3DRenderable } from './Model3DViewer';

const isWeb = Platform.OS === 'web';

function getPrimaryAssetUrl(file) {
  if (!file) return null;
  return file.cdnPath || file.url || file.uri || null;
}

function getThumbnailUrl(file) {
  if (!file) return null;
  return file.thumbnailPath || file.thumbnail || null;
}

function getFormat(file) {
  const meta = file?.metadata?.['3d'] || file?.metadata?.threed || {};
  if (meta.format) return String(meta.format).toLowerCase();
  const url = getPrimaryAssetUrl(file);
  if (!url) return null;
  const cleaned = url.split('?')[0].split('#')[0];
  const ext = cleaned.split('.').pop()?.toLowerCase();
  return ext || null;
}

function getAlternateFormats(file) {
  const meta = file?.metadata?.['3d'] || file?.metadata?.threed || {};
  return Array.isArray(meta.alternateFormats) ? meta.alternateFormats : [];
}

function getIosSrc(file) {
  const alts = getAlternateFormats(file);
  const usdz = alts.find(f => (f.format || '').toLowerCase() === 'usdz');
  return usdz?.url || null;
}

function Model3DCardContent({ file, onPress, autoActivate = false, viewerOnly = false }) {
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const modelUrl = getPrimaryAssetUrl(file);
  const thumbUrl = getThumbnailUrl(file);
  const format = getFormat(file);
  const iosSrc = getIosSrc(file);
  const alternateFormats = getAlternateFormats(file);

  // Mirror the viewer's own format coverage. Asking the user to "tap to view"
  // a .fbx and then dropping them on a "Preview unavailable" placeholder is a
  // dead-end — gate activation on whether we'd actually render anything.
  const canRender = useMemo(
    () => isModel3DRenderable(modelUrl, alternateFormats),
    [modelUrl, alternateFormats],
  );

  // viewerOnly callers (e.g. lightbox) want the viewer regardless — they're
  // already past the "is it worth opening?" decision.
  const [isActive, setIsActive] = useState((autoActivate && canRender) || viewerOnly);

  const formatLabel = format ? format.toUpperCase() : '3D';

  const handleActivate = useCallback((e) => {
    e?.stopPropagation?.();
    if (isActive) {
      onPress?.();
      return;
    }
    if (!canRender) {
      // Format isn't renderable in-app — surface the download affordance
      // instead of activating into a useless viewer.
      onPress?.();
      return;
    }
    setIsActive(true);
  }, [isActive, canRender, onPress]);

  // If the viewer errors at runtime (CORS, malformed mesh, CDN blip, …) we want
  // to flip back to the thumbnail rather than leave the user stranded on the
  // generic "Could not load 3D model" state.
  const handleViewerError = useCallback(() => {
    if (viewerOnly) return; // lightbox-style hosts handle their own fallback UI
    setIsActive(false);
    addToast?.(t('models3d.previewUnavailable') || 'Preview unavailable — download to view', 'info');
  }, [viewerOnly, addToast, t]);

  const handleDownload = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (!modelUrl) return;
    setIsDownloading(true);
    try {
      // For OBJ assets, download3DAsset bundles obj+mtl+texture into a zip so
      // the user's local previewer renders textures instead of an untextured
      // white blob. Other formats (glb, etc.) pass straight through.
      const baseName = file?.fileName || `model-${Date.now()}`;
      await download3DAsset({
        modelUrl,
        alternateFormats,
        baseName,
        options: {
          onSuccess: () => addToast?.(t('media.downloadComplete') || t('media.savedToPhotos'), 'success'),
          onError: (msg) => addToast?.(msg || t('media.saveFailed'), 'error'),
        },
      });
    } finally {
      setIsDownloading(false);
    }
  }, [modelUrl, file, alternateFormats, addToast, t]);

  const showThumbnail = !isActive && !!thumbUrl;
  const showPlaceholder = !isActive && !thumbUrl;

  const formatBadgeContent = useMemo(() => (
    <View style={styles.formatBadge}>
      <Icon name="cube" size={10} color="#fff" solid />
      <Text size="xxs" weight="semiBold" color="white">{formatLabel}</Text>
    </View>
  ), [formatLabel]);

  return (
    <View
      style={styles.container}
      onMouseEnter={isWeb ? () => setIsHovered(true) : undefined}
      onMouseLeave={isWeb ? () => setIsHovered(false) : undefined}
      onClick={isWeb ? (e) => e?.stopPropagation?.() : undefined}
    >
      {isActive ? (
        <Model3DViewer
          modelUrl={modelUrl}
          posterUrl={thumbUrl}
          iosSrc={iosSrc}
          alternateFormats={alternateFormats}
          autoRotate
          style={styles.fill}
          onError={handleViewerError}
        />
      ) : showThumbnail ? (
        <Pressable style={styles.fill} onPress={canRender ? handleActivate : handleDownload}>
          <Image
            source={{ uri: thumbUrl }}
            style={styles.fill}
            contentFit="cover"
            transition={200}
          />
          <View style={styles.thumbnailOverlay} pointerEvents="none">
            <View style={styles.viewBadge}>
              <Icon name={canRender ? 'cube' : 'arrow-down-to-line'} size={20} color="#fff" solid />
              <Text size="s" weight="semiBold" color="white" style={{ marginTop: 4 }}>
                {canRender ? t('models3d.tapToView') : (t('models3d.tapToDownload') || `Download ${formatLabel}`)}
              </Text>
            </View>
          </View>
        </Pressable>
      ) : showPlaceholder ? (
        <Pressable style={[styles.fill, styles.placeholder]} onPress={canRender ? handleActivate : handleDownload}>
          <Icon name={canRender ? 'cube' : 'arrow-down-to-line'} size={36} color="rgba(255,255,255,0.65)" solid />
          <Text size="s" weight="medium" color="white" style={{ marginTop: 8 }}>
            {canRender ? t('models3d.tapToView') : (t('models3d.tapToDownload') || `Download ${formatLabel}`)}
          </Text>
          {format ? (
            <Text size="xxs" color="white" style={{ marginTop: 2, opacity: 0.7 }}>
              {formatLabel}
            </Text>
          ) : null}
        </Pressable>
      ) : null}

      {!viewerOnly && (
        <View style={styles.formatBadgeContainer} pointerEvents="none">
          {formatBadgeContent}
        </View>
      )}

      {!viewerOnly && modelUrl && (
        <Pressable
          onPress={handleDownload}
          disabled={isDownloading}
          style={[
            styles.downloadButton,
            (isWeb && !isHovered) && styles.downloadButtonHidden,
          ]}
        >
          {isDownloading ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Icon name="arrow-down-to-line" size={16} color="#fff" />
          )}
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  fill: {
    width: '100%',
    height: '100%',
  },
  placeholder: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(20, 24, 28, 0.85)',
  },
  thumbnailOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
  viewBadge: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    ...(isWeb && { backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }),
  },
  formatBadgeContainer: {
    position: 'absolute',
    top: 8,
    left: 8,
    zIndex: 2,
  },
  formatBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
  },
  downloadButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3,
    ...(isWeb && { transition: 'opacity 0.2s ease', cursor: 'pointer' }),
  },
  downloadButtonHidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
});

Model3DCardContent.propTypes = {
  file: PropTypes.shape({
    cdnPath: PropTypes.string,
    url: PropTypes.string,
    thumbnailPath: PropTypes.string,
    fileName: PropTypes.string,
    metadata: PropTypes.object,
  }).isRequired,
  onPress: PropTypes.func,
  autoActivate: PropTypes.bool,
  viewerOnly: PropTypes.bool,
};

export default React.memo(Model3DCardContent);
